// Function index:
//   PartyRailHandle          — live-update handle returned by mountPartyRail.
//   mountPartyRail(opts)     — render one card per party PC into #party-rail
//       (self first, with class/level), and return a handle to push live HP
//       and presence (online/typing) updates.
//
// Why: the left-hand player rail. HP feeds off character.hp_changed; the
// online/typing dot feeds off presence.update. Self is always shown online.

import type { BootstrapCharacter, PartyCharacter } from '../api.js';
import type { PresenceUser } from '../chat.js';

export interface PartyRailHandle {
  updateHp(charId: number, hp: number, max_hp: number): void;
  setPresence(users: PresenceUser[]): void;
}

interface CardRefs {
  root: HTMLElement;
  fill: HTMLElement;
  hptext: HTMLElement;
  userId: number | null;
  isSelf: boolean;
}

export function mountPartyRail(opts: {
  selfCharId: number | null;
  self: BootstrapCharacter | null;
  members: PartyCharacter[];
  // Shown as a click-to-copy line under the cards. The code used to live
  // ONLY in title-screen meta text — co-op was undiscoverable from in-game.
  inviteCode?: string | null;
}): PartyRailHandle {
  const host = document.getElementById('party-rail')!;
  host.innerHTML = '';
  const head = document.createElement('div');
  head.className = 'rail-head';
  head.textContent = 'Party';
  host.appendChild(head);

  const refs = new Map<number, CardRefs>();

  // Self first, with class/level when known.
  if (opts.self) {
    const sub = [opts.self.character_class, opts.self.level ? `Lv ${opts.self.level}` : null]
      .filter(Boolean).join(' · ') || null;
    addCard(opts.self.id, opts.self.name, sub, opts.self.hp, opts.self.max_hp, null, true);
  }
  for (const m of opts.members) {
    if (m.id === opts.selfCharId) continue;
    addCard(m.id, m.name, null, m.hp, m.max_hp, m.user_id, false);
  }

  if (opts.inviteCode) {
    const code = opts.inviteCode;
    const invite = document.createElement('button');
    invite.type = 'button';
    invite.id = 'rail-invite';
    invite.className = 'rail-invite';
    invite.title = 'Click to copy — a friend joins from the title screen with this code';
    invite.textContent = `invite a friend: ${code}`;
    invite.addEventListener('click', () => {
      const clipboard = navigator.clipboard;
      if (!clipboard) return;
      void clipboard.writeText(code).then(() => {
        invite.textContent = 'copied — share it!';
        setTimeout(() => { invite.textContent = `invite a friend: ${code}`; }, 2000);
      }).catch(() => { /* clipboard denied: the code stays visible to copy by hand */ });
    });
    host.appendChild(invite);
  }

  function addCard(
    id: number, name: string, sub: string | null,
    hp: number, max_hp: number, userId: number | null, isSelf: boolean,
  ): void {
    const card = document.createElement('div');
    card.className = 'pc-card' + (isSelf ? ' is-self' : ' offline');
    card.innerHTML = `
      <div class="pc-top">
        <span class="pc-dot"></span>
        <span class="pc-name">${esc(name)}</span>
      </div>
      ${sub ? `<div class="pc-class">${esc(sub)}</div>` : ''}
      <div class="pc-hpbar"><div class="pc-hpfill"></div></div>
      <div class="pc-hptext"></div>
    `;
    host.appendChild(card);
    const ref: CardRefs = {
      root: card,
      fill: card.querySelector('.pc-hpfill')!,
      hptext: card.querySelector('.pc-hptext')!,
      userId,
      isSelf,
    };
    refs.set(id, ref);
    setHp(ref, hp, max_hp);
  }

  function setHp(ref: CardRefs, hp: number, max_hp: number): void {
    const pct = max_hp > 0 ? Math.max(0, Math.min(100, (hp / max_hp) * 100)) : 0;
    ref.fill.style.width = `${pct}%`;
    ref.fill.classList.toggle('low', hp <= max_hp * 0.33 || hp === 0);
    ref.hptext.textContent = `${hp}/${max_hp}`;
  }

  return {
    updateHp(charId, hp, max_hp) {
      const ref = refs.get(charId);
      if (ref) setHp(ref, hp, max_hp);
    },
    setPresence(users) {
      const byUser = new Map(users.map((u) => [u.user_id, u]));
      for (const ref of refs.values()) {
        if (ref.isSelf) continue; // self is always "here"
        const u = ref.userId != null ? byUser.get(ref.userId) : undefined;
        ref.root.classList.toggle('offline', !u);
        ref.root.classList.toggle('typing', !!u?.typing);
      }
    },
  };
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
